import { useTranslation } from "react-i18next";
import { useClientContactInformation } from "../../../graphql/hooks/client/useClientContactInformation";
import SmallLoadingSpinner from "../../atoms/SmallLoadingSpinner";
import Alert from "../Alert";
import styled from "styled-components";
import { theme } from "../../../theme";
import Small from "../../atoms/typography/Small";
import BodySmall from "../../atoms/typography/BodySmall";
import {
  AddressPostalIcon,
  AddressLegalIcon,
  AddressVisitIcon,
} from "../../atoms/icons/AddressIcons";

interface Props {
  clientIdentifier: string;
  useTitles: boolean;
  useBackgroundColor: boolean;
}

const BlueBox = styled.div<{ useBackgroundColor: boolean }>`
  background-color: ${(props) =>
    props.useBackgroundColor ? theme.colors.primaryBg : "transparent"};
  padding: ${(props) => (props.useBackgroundColor ? "16px" : "0 16px")};
  border-radius: ${(props) => (props.useBackgroundColor ? "8px" : "0")};
  border: none;
  border-left: ${(props) =>
    props.useBackgroundColor ? "none" : `2px solid ${theme.colors.primary60}`};
`;

const ClientGridContainer = styled.div`
  display: flex;
  gap: 32px;
`;

const TypeCircleIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: ${theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ClientAddresses = ({
  clientIdentifier,
  useTitles,
  useBackgroundColor,
}: Props) => {
  const { t } = useTranslation();
  const { client, error, loading } =
    useClientContactInformation(clientIdentifier);

  return (
    <div>
      {loading && <SmallLoadingSpinner />}
      {error ? (
        <Alert
          type="warning"
          title={t("errors.couldNotFetchClientAddresses")}
          message={error.message}
          small
        />
      ) : (
        <div>
          <ClientGridContainer>
            {client?.addresses?.legal && (
              <BlueBox useBackgroundColor={useBackgroundColor}>
                {useTitles && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <TypeCircleIcon>
                      <AddressLegalIcon color="white" />
                    </TypeCircleIcon>
                    <Small fontweight={600} color={theme.colors.primary}>
                      LEGAL
                    </Small>
                  </div>
                )}
                {client.addresses.legal.co && (
                  <BodySmall color={theme.colors.textSecondary}>
                    <i>{client.addresses.legal.co}</i>
                  </BodySmall>
                )}
                <BodySmall color={theme.colors.textSecondary}>
                  {client.addresses.legal.street}
                </BodySmall>
                <BodySmall color={theme.colors.textSecondary}>
                  {client.addresses.legal.postalCode}{" "}
                  {client.addresses.legal.city}
                </BodySmall>
              </BlueBox>
            )}
            {client?.addresses?.postal && (
              <BlueBox useBackgroundColor={useBackgroundColor}>
                {useTitles && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <TypeCircleIcon>
                      <AddressPostalIcon color="white" />
                    </TypeCircleIcon>
                    <Small fontweight={600} color={theme.colors.primary}>
                      POSTAL
                    </Small>
                  </div>
                )}
                {client.addresses.postal.co && (
                  <BodySmall color={theme.colors.textSecondary}>
                    <i>{client.addresses.postal.co}</i>
                  </BodySmall>
                )}
                <BodySmall color={theme.colors.textSecondary}>
                  {client.addresses.postal.street}
                </BodySmall>
                <BodySmall color={theme.colors.textSecondary}>
                  {client.addresses.postal.postalCode}{" "}
                  {client.addresses.postal.city}
                </BodySmall>
              </BlueBox>
            )}
            {client?.addresses?.visiting && (
              <BlueBox useBackgroundColor={useBackgroundColor}>
                {useTitles && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <TypeCircleIcon>
                      <AddressVisitIcon color="white" />
                    </TypeCircleIcon>
                    <Small fontweight={600} color={theme.colors.primary}>
                      VISITING
                    </Small>
                  </div>
                )}
                {client.addresses.visiting.co && (
                  <BodySmall color={theme.colors.textSecondary}>
                    <i>{client.addresses.visiting.co}</i>
                  </BodySmall>
                )}
                <BodySmall color={theme.colors.textSecondary}>
                  {client.addresses.visiting.street}
                </BodySmall>
                <BodySmall color={theme.colors.textSecondary}>
                  {client.addresses.visiting.postalCode}{" "}
                  {client.addresses.visiting.city}
                </BodySmall>
              </BlueBox>
            )}

            {!client?.addresses && (
              <BodySmall>Inga adresser hittades</BodySmall>
            )}
          </ClientGridContainer>
        </div>
      )}
    </div>
  );
};

export default ClientAddresses;
